// src/utils/escalationState.js
//
// What state an escalation is in, as the doctor and navigator queues need it.
// One place, so the queue (components/EscalationQueue.jsx), the navigator
// Dashboard stats and the pages' overdue badges cannot drift apart.
//
// Two facts are deliberately separate:
//   - ACKNOWLEDGED — somebody answered the 2h SLA (usually the navigator).
//   - doctorReviewedAt — a DOCTOR has acted on it. A navigator's acknowledgement
//     does not set this, so the escalation stays on the doctor's list until a
//     doctor has looked at it.

// The Constitution §III: a CRITICAL escalation must be acknowledged within 2h.
export const SLA_HOURS = 2;

export const isUnacknowledged = e => ['PENDING', 'NOTIFIED', 'ESCALATED'].includes(e.status);

export const isSnoozed = e => e.status === 'ACKNOWLEDGED'
  && !!e.snoozedUntil && new Date(e.snoozedUntil).getTime() > Date.now();

export function isSlaBreached(e) {
  if (!isUnacknowledged(e)) return false;
  if (e.status === 'ESCALATED') return true;
  if (!e.slaDueAt) return false;
  return Date.now() >= new Date(e.slaDueAt).getTime();
}

// The navigator's part is finished once a suggested specialist is booked.
export const navigatorWorkDone = e => !!(e.suggestedSpecialty && e.specialistBookedAt);

// Specialist suggested by a doctor, not yet booked by the navigator.
export const needsSpecialistBooking = e => !!e.suggestedSpecialty && !e.specialistBookedAt;

/**
 * Which doctor tab an unresolved/resolved escalation belongs to. Every row falls
 * in exactly one:
 *   resolved   — closed
 *   snoozed    — deferred until snoozedUntil
 *   open       — no doctor has reviewed it yet (includes ones a navigator has
 *                already acknowledged)
 *   navigator  — a doctor handed it to a navigator and that work is outstanding
 *   progress   — a doctor is handling it, or the navigator's part is done and
 *                it is waiting for the doctor to close
 */
export function doctorTab(e) {
  if (e.status === 'RESOLVED') return 'resolved';
  if (isSnoozed(e)) return 'snoozed';
  if (isUnacknowledged(e) || !e.doctorReviewedAt) return 'open';
  if (e.assignedNavigatorId && !navigatorWorkDone(e)) return 'navigator';
  return 'progress';
}

/**
 * Counts for the navigator Dashboard.
 * @param active every unresolved escalation the navigator can see
 * @param mine   the unresolved ones assigned to this navigator
 */
export function navigatorStats(active, mine) {
  const unacked = active.filter(isUnacknowledged);
  const mineLive = mine.filter(e => !isSnoozed(e));
  return {
    assigned: mineLive.length,
    toBook: mineLive.filter(needsSpecialistBooking).length,
    needsResponse: unacked.length,
    overdue: unacked.filter(isSlaBreached).length,
    inProgress: active.filter(e => e.status === 'ACKNOWLEDGED' && !isSnoozed(e)).length,
  };
}
